import { createTheme, type Theme } from '@mui/material';

/** Latin: Inter. Arabic: Readex Pro (same pairing as market). */
export const HELP_FONT_FAMILY_EN =
  '"Inter", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif';

export const HELP_FONT_FAMILY_AR =
  '"Readex Pro", "Segoe UI", Tahoma, "Helvetica Neue", Arial, sans-serif';

export const getHelpFontFamily = (language: string): string =>
  language.startsWith('ar') ? HELP_FONT_FAMILY_AR : HELP_FONT_FAMILY_EN;

/** Readex Pro reads heavier than Inter at the same numeric weight — normalize for parity. */
export const getHelpFontWeight = (
  language: string,
  weight: 400 | 500 | 600
): 400 | 500 | 600 => {
  if (!language.startsWith('ar')) return weight;
  if (weight === 600) return 500;
  return weight;
};

export const HELP_PAGE_TITLE_SX = {
  fontSize: '1.5rem',
  lineHeight: 1.3,
  letterSpacing: 0,
} as const;

export const HELP_KNOWLEDGE_CARD_TITLE_SX = {
  fontSize: '1rem',
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

export const HELP_KNOWLEDGE_CARD_TITLE_MIN_HEIGHT = '2.9rem';

export const HELP_KNOWLEDGE_CARD_LINK_SX = {
  fontSize: '0.8125rem',
  lineHeight: 1.45,
  letterSpacing: 0,
} as const;

export const HELP_BODY_SX = {
  fontSize: '0.9375rem',
  lineHeight: 1.75,
  letterSpacing: 0,
} as const;

export const helpTextRenderingSx = {
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  textRendering: 'optimizeLegibility',
} as const;

export const getKnowledgeCardTitleSx = (language: string) => ({
  ...HELP_KNOWLEDGE_CARD_TITLE_SX,
  ...helpTextRenderingSx,
  fontFamily: getHelpFontFamily(language),
  fontWeight: getHelpFontWeight(language, 600),
  minHeight: HELP_KNOWLEDGE_CARD_TITLE_MIN_HEIGHT,
});

export const getKnowledgeCardLinkSx = (language: string) => ({
  ...HELP_KNOWLEDGE_CARD_LINK_SX,
  ...helpTextRenderingSx,
  fontFamily: getHelpFontFamily(language),
  fontWeight: getHelpFontWeight(language, 600),
});

const withHelpFontFamily = (parentTheme: Theme, language: string) => {
  const fontFamily = getHelpFontFamily(language);
  const { typography } = parentTheme;

  return {
    ...typography,
    fontFamily,
    h4: { ...typography.h4, fontFamily, letterSpacing: 0 },
    h6: { ...typography.h6, fontFamily, letterSpacing: 0 },
    subtitle1: {
      ...typography.subtitle1,
      fontFamily,
      fontSize: HELP_KNOWLEDGE_CARD_TITLE_SX.fontSize,
      lineHeight: HELP_KNOWLEDGE_CARD_TITLE_SX.lineHeight,
      letterSpacing: 0,
    },
    subtitle2: {
      ...typography.subtitle2,
      fontFamily,
      fontSize: HELP_KNOWLEDGE_CARD_LINK_SX.fontSize,
      lineHeight: HELP_KNOWLEDGE_CARD_LINK_SX.lineHeight,
      letterSpacing: 0,
    },
    body1: {
      ...typography.body1,
      fontFamily,
      fontSize: HELP_BODY_SX.fontSize,
      lineHeight: HELP_BODY_SX.lineHeight,
      letterSpacing: 0,
    },
    body2: { ...typography.body2, fontFamily, letterSpacing: 0 },
    caption: { ...typography.caption, fontFamily, letterSpacing: 0 },
    button: { ...typography.button, fontFamily, letterSpacing: 0 },
  };
};

export const createHelpTheme = (parentTheme: Theme, language: string) => {
  const direction = language.startsWith('ar') ? 'rtl' : 'ltr';
  const fontFamily = getHelpFontFamily(language);

  return createTheme(parentTheme, {
    direction,
    typography: withHelpFontFamily(parentTheme, language),
    components: {
      ...parentTheme.components,
      MuiCard: {
        styleOverrides: {
          root: {
            fontFamily,
            transform: 'none',
            '&:hover': {
              transform: 'none',
              boxShadow: parentTheme.shadows[1],
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            fontFamily,
            letterSpacing: 0,
            ...helpTextRenderingSx,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            letterSpacing: 0,
            ...helpTextRenderingSx,
          },
        },
      },
    },
  });
};

export const helpTypographySx = (language: string) => {
  const fontFamily = getHelpFontFamily(language);

  return {
    fontFamily,
    ...helpTextRenderingSx,
    '& .help-kb-card': {
      fontFamily,
      ...helpTextRenderingSx,
    },
    '& .help-kb-card-title': getKnowledgeCardTitleSx(language),
    '& .help-kb-card-link': getKnowledgeCardLinkSx(language),
    '& .MuiButton-root, & .MuiButtonBase-root, & .MuiInputBase-root, & .MuiInputBase-input':
      {
        fontFamily: 'inherit',
        ...helpTextRenderingSx,
      },
  };
};
